import { Link } from "react-router-dom";
import { Lock, ArrowRight, X } from "lucide-react";

/**
 * Modal shown when a user hits a members-only feature.
 * Wired into `formatApiErrorDetail` / api interceptor — when the backend
 * returns 402 with `code: "tier_upgrade_required"`, the FE pops this modal
 * instead of showing the raw error toast.
 */
export default function TierUpgradeModal({ detail, onClose }) {
  if (!detail || detail.code !== "tier_upgrade_required") return null;

  return (
    <div
      className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="tier-upgrade-modal"
    >
      <div className="brutal-card bg-white max-w-md w-full p-6 sm:p-8 relative">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[color:var(--paper-muted)] border-[2px] border-[color:var(--ink)] flex items-center justify-center hover:bg-[color:var(--cream-deep)]"
          data-testid="tier-upgrade-modal-close"
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-2 mb-3">
          <Lock size={18} className="text-[color:var(--coral-deep)]" />
          <span className="display text-xs uppercase tracking-[0.25em] font-bold text-[color:var(--coral-deep)]">
            Membership
          </span>
        </div>

        <h2 className="display text-2xl sm:text-3xl font-extrabold leading-tight mb-3">
          Make it yours.
        </h2>

        <p className="text-[color:var(--ink-soft)] mb-2">
          {detail.message ||
            "This feature is part of Membership ($7/month or $70/year)."}
        </p>

        <ul className="text-sm space-y-1.5 mb-5">
          <li>✅ Custom voice recording</li>
          <li>✅ Real photos on tiles</li>
          <li>✅ Edit / add tiles</li>
          <li>✅ AI sentence prediction</li>
          <li>✅ AI-generated symbols</li>
          <li>✅ Helper invites (therapist, doctor, school)</li>
          <li>✅ Communication history</li>
          <li>✅ Family Notes (parent → child)</li>
        </ul>

        <div className="flex gap-2">
          <Link
            to="/pricing"
            onClick={onClose}
            className="pill-btn primary flex-1 justify-center"
            data-testid="tier-upgrade-modal-cta"
          >
            See Membership — $7/mo or $70/yr <ArrowRight size={14} />
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="pill-btn"
            data-testid="tier-upgrade-modal-skip"
          >
            Not now
          </button>
        </div>

        <p className="text-[11px] text-[color:var(--ink-soft)] mt-4">
          Your starter board still works. Cancel anytime, keep what you've already created.
        </p>
      </div>
    </div>
  );
}
